import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Slide,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CommonTextField from './CommonTextField';
import { iataValidate } from '../../../utils';

// ============================================================
// PosModal – Air Canada supported POS selection (2 steps)
//
// Adapted from the seller-registration "super modal":
//   Step 0 – search + country list (left) and a selected-chips panel (right)
//   Step 1 – per-country IATA entry, then Apply
//
// Driven by the admin app's registrationData (sup / nonsup) instead of the
// registration portal's i18n/redux slice.
//
// Props:
//   open, onClose
//   supList   : Array<{ pos_code, country }>   (Air Canada supported POS)
//   onApply(items)  items: Array<{ code, name, flag, iata }>
//   initialSelected : string[]              (pre-selected pos codes)
//   initialIata     : Record<code, iata>    (pre-filled IATA numbers)
// ============================================================

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={{ enter: 500, exit: 250 }} />;
});

// Flags live in /public/flags as 2-letter codes (UK uses GB asset).
const flagSrc = (code) =>
  process.env.PUBLIC_URL + `/flags/${code === 'UK' ? 'GB' : code}.png`;

const FlagImg = ({ code }) =>
  code && code !== 'OT' ? (
    <img
      src={flagSrc(code)}
      alt=""
      style={{ width: 24, height: 16, objectFit: 'contain', display: 'block' }}
    />
  ) : null;

const OTHER_CODE = 'OT';

const PosModal = ({
  open,
  onClose,
  supList = [],
  onApply,
  initialSelected = [],
  initialIata = {},
  enableIataStep = true,
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');

  const getRegionOrder = (name) => {
    const n = name.toUpperCase();
    if (n.includes('NORTH') || n.includes('NORD')) return 0;
    if (n.includes('SOUTH') || n.includes('SUD')) return 1;
    if (n.includes('EUROPE')) return 2;
    if (n.includes('ASIA') || n.includes('ASIE')) return 3;
    if (n.includes('OCEANIA') || n.includes('OCÉANIE')) return 4;
    return 5;
  };

  // Build the full country list once from supList + an "Other" entry.
  const allCountries = useMemo(() => {
    const list = Object.values([...supList]

      .reduce((acc, item) => {
        if (!item.region) {
          return acc; // skip null/undefined/empty regions
        }
        
        if (!acc[item.region]) {
          acc[item.region] = {
            countries: [],
            name: item.region
          }
        }

        acc[item.region].countries.push({
          id: item.pos_code,
          name: item.country,

        })
        return acc
      }, {})).sort((a, b) => getRegionOrder(a.name) - getRegionOrder(b.name));
    list.push({ countries: [{ name: 'Other', id: 'OT' }], name: 'Other Markets', });
    return list;
  }, [supList]);
 
  const totalCount = allCountries.reduce((acc,arr)=>{const coutries= arr.countries; return acc+coutries.length},0)

  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState(() => new Set(initialSelected));
  const [iataValues, setIataValues] = useState({ ...initialIata });
  const [search, setSearch] = useState('');
  const [applyAttempted, setApplyAttempted] = useState(false);

  // Re-seed local state whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setSelected(new Set(initialSelected));
      setIataValues({ ...initialIata });
      setStep(0);
      setSearch('');
      setApplyAttempted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCountries;
    return allCountries.map((r) => ({
      ...r,
      countries: r.countries.filter((c) => c.name.toLowerCase().includes(q))
    })).filter((r) => r.countries.length > 0);
  }, [search, allCountries]);

  const toggle = useCallback((code) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);
  

  const handleSelectAll = useCallback(
    (region) => {
      const allChecked = region.countries.every((c) => selected.has(c.id));
      setSelected((prev) => {
        const next = new Set(prev);
        region.countries.forEach((c) =>
          allChecked ? next.delete(c.id) : next.add(c.id)
        );
        return next;
      });
    },
    [selected]
  );

  const clearAll = useCallback(() => setSelected(new Set()), []);
  // Dedupe by id: a pos_code can appear more than once in supList (e.g. under
  // multiple regions), which would otherwise emit duplicate POS items.
  const allCodes = useMemo(() => {
    const seen = new Set();
    return allCountries
      .flatMap((r) => r.countries)
      .filter((c) => (seen.has(c.id) ? false : seen.add(c.id)));
  }, [allCountries]);
  const selectedCountries = allCodes.filter((c) => selected.has(c.id));
  const iataCountries = selectedCountries.filter((c) => c.id !== OTHER_CODE);
  const shouldShowIataStep = enableIataStep && iataCountries.length > 0;

  // Detect whether the user has actually changed anything from the initial
  // selection/IATA values, so Next/Apply can stay disabled when there's
  // nothing to save.
  const isSelectionChanged = useMemo(() => {
    const initialSet = new Set(initialSelected);
    if (selected.size !== initialSet.size) return true;
    for (const id of selected) {
      if (!initialSet.has(id)) return true;
    }
    return false;
  }, [selected, initialSelected]);

  const isIataChanged = useMemo(
    () =>
      iataCountries.some(
        (c) => (iataValues[c.id] || '') !== (initialIata[c.id] || '')
      ),
    [iataCountries, iataValues, initialIata]
  );

  const hasChanges = isSelectionChanged || isIataChanged;

  const handleIataChange = useCallback((code, value) => {
    const sanitized = value.replace(/[^0-9]/g, '').slice(0, 8);
    setIataValues((prev) => ({ ...prev, [code]: sanitized }));
  }, []);

  const handleNext = () => {
    if (selected.size === 0) return;
    if (!shouldShowIataStep) {
      emitApply();
      return;
    }
    setStep(1);
  };

  const emitApply = () => {
    const items = selectedCountries.map((c) => ({
      ...c,
      iata: iataValues[c.id] || '',
      flag: flagSrc(c.id)
    }));
    console.log(items,"onAPPLY")
    onApply?.(items);
  };

  const handleApply = () => {
    const hasInvalid = iataCountries.some((c) => {
      const v = iataValues[c.id] || '';
      return v.length > 0 && !iataValidate(v);
    });
    if (hasInvalid) {
      setApplyAttempted(true);
      return;
    }
    emitApply();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      TransitionComponent={SlideUp}
      sx={{ alignItems: 'flex-end' }}
      PaperProps={{
        sx: {
          width: '100vw',
          maxWidth: '100vw',
          m: 0,
          borderRadius: { xs: 0, md: '16px 16px 0 0' },
          height: { xs: '100dvh', sm: '90vh' },
          maxHeight: { xs: '100dvh', sm: '90vh' },
          position: 'fixed',
          bottom: 0,
          top: { xs: 0, sm: 'auto' }
        }
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontStyle: 'bold',
          fontSize: { xs: '20px', sm: '24px', md: '28px' },
          lineHeight: '36px',
          letterSpacing: '0px',
          textAlign: 'center',
          color: 'var(--Sem-text-colour-text-primary, #000000)',
          border: 0,
          mt: '40px',
          mb: { xs: 1, sm: 2, md: 3 },
          ...(step === 1 ? { pb: 1.5 } : { pb: 1 }),
        }}
      >
        {step === 0 ? 'Select supported POS' : 'Enter IATA numbers'}
        {step === 1 && shouldShowIataStep && (
          <Typography sx={{
            fontWeight: 400,
            fontStyle: 'normal',
            fontSize: { xs: '14px', sm: '18px' },
            lineHeight: '24px',
            letterSpacing: '0px',
            textAlign: 'center',
            color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)',
            background: 'transparent',
            mt: 1
          }}>
            IATA numbers are optional. Leave blank where not applicable.
          </Typography>
        )}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: '#1460AA' }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      {step === 0 ? (
        <>
          <DialogContent sx={{
            display: 'flex',
            overflow: 'auto',
            justifyContent: 'center',
            width: '100%',
            flex: 1,
            minHeight: 0,
            p: 0
          }}>
            <Grid container
              maxWidth={{ md: '1200px', xs: '704px' }}
              width="100%"
              gap={3}
              flexWrap={{ xs: 'wrap', md: 'nowrap' }}
              alignContent={{ xs: 'flex-start', md: 'stretch' }}
              overflow={{ xs: 'visible', md: 'hidden' }}
              mx={{ xs: 4 }}

            >
              {/* Left: search + list */}
              <Grid
                item
                xs={12}
                md={8}
                gap={4}
                py={0}

                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: { xs: 'auto', md: 0 }
                }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search country"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#1460AA' }} />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Clear search"
                          size="small"
                          onClick={() => setSearch('')}
                          sx={{ p: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: 18, color: '#6D6D6F' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    pl: 0,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { border: 'none' }
                    },
                    '& .MuiInputBase-root': {
                      borderBottom: '1px solid #C9CACC',
                      borderRadius: 0,
                      pl: 0
                    },
                    '& .MuiInputBase-input': {
                      pl: 0,

                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '20px',
                      letterSpacing: '0px',
                      verticalAlign: 'middle',
                      color: 'black',
                      ml: 1
                    },
                    '& .MuiInputBase-input::placeholder': {

                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '20px',
                      letterSpacing: '0px',
                      color: '#6D6D6F',
                      opacity: 1
                    }
                  }}
                />



                <Box sx={{
                  overflowY: { xs: 'visible', md: 'auto' },
                  gap: 4,
                  pb: 2,
                  pt: '8px',
                  px: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'transparent transparent',
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 3 },
                  '&:hover': { scrollbarColor: '#C9CACC transparent' },
                  '&:hover::-webkit-scrollbar-thumb': { background: '#C9CACC' }
                }}>
                  {filtered.map((c) => {
                    const allChecked = c.countries.every((c) =>
                      selected.has(c.id)
                    );
                    return (
                      <Box
                        key={c.name}
                        gap={3}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}

                      >

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >

                          <Typography sx={{
                            fontWeight: 700,
                            fontStyle: 'bold',
                            fontSize: { xs: '16px', sm: '18px' },
                            lineHeight: '24px',
                            letterSpacing: '0px',
                            textTransform: 'uppercase',
                            color: '#000000)',
                            borderRadius: '4px',
                            display: 'inline-block'
                          }}>
                            {c.name}
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleSelectAll(c)}
                            disableRipple
                            sx={{
                              minWidth: 0,
                              p: 0,
                              fontWeight: 500,
                              fontStyle: 'normal',
                              fontSize: { xs: '14x', sm: '16px' },
                              lineHeight: '22px',
                              textTransform: 'none',
                              letterSpacing: '0px',
                              textDecoration: 'underline',
                              textDecorationStyle: 'solid',
                              textDecorationThickness: 0,
                              textDecorationOffset: 0,
                              color: '#1460AA)',
                              '&:hover': {
                                backgroundColor: 'transparent'
                              }
                            }}
                          >
                            {allChecked
                              ? "Deselect all"
                              : `Select all (${c.countries.length})`
                            }
                          </Button>

                        </Stack>
                        <Box
                          display={'flex'}
                          flexDirection={'column'}
                          gap={2}>
                          {c.countries.map((country) => (
                            <Box key={country.name}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={1}
                                component="label"
                              >
                                <Checkbox
                                  size="small"
                                  checked={selected.has(country.id)}
                                  onChange={() => { toggle(country.id) }}
                                  sx={{
                                    p: 0,
                                    width: 24,
                                    height: 24,
                                    opacity: 1,
                                    '& .MuiSvgIcon-root': { width: 24, height: 24 },
                                    color: '#1460AA',
                                    '&.Mui-checked': { color: '#1460AA' },
                                    '&.Mui-focusVisible': {
                                      backgroundColor: 'transparent',
                                      outline: '1px solid black',
                                      outlineOffset: '2px',
                                      '& .MuiTouchRipple-root': { display: 'none' }
                                    }
                                  }}
                                />

                                <FlagImg code={country.id} />
                                <Typography>
                                  {country.name}
                                </Typography>
                              </Stack>

                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )
                  })}
                  {filtered.length === 0 && (
                    <Typography sx={{ fontSize: '14px', color: '#6D6D6F', p: 2 }}>
                      No countries found.
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Right: selected chips */}
              <Grid item
                xs={12}
                md={4}
                gap={4}
                p={4}
                pb={0}
                order={{ xs: 1, md: 2 }}
                sx={{
                  bgcolor: '#F1F7FB',
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'visible'
                }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography sx={{

                    fontWeight: 700,
                    fontStyle: 'bold',
                    fontSize: { xs: '16px', sm: '18px' },
                    lineHeight: '24px',
                    letterSpacing: '0px',
                    textTransform: 'uppercase',
                    color: '#000000'
                  }}>
                    Selected ({selected.size})
                  </Typography>
                  <Button
                    size="small"
                    onClick={clearAll}
                    disabled={selected.size === 0}
                    sx={{
                      textTransform: 'none',
                      minWidth: 0,
                      p: 0,

                      fontWeight: 500,
                      fontStyle: 'normal',
                      fontSize: { xs: '14x', sm: '16px' },
                      lineHeight: '22px',
                      letterSpacing: '0px',
                      textDecoration: 'underline',
                      textDecorationStyle: 'solid',
                      textDecorationThickness: 0,
                      textDecorationOffset: 0,
                      color: '#1460AA'
                    }}
                  >
                    Clear all
                  </Button>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedCountries.map((c) => (
                    <Chip
                      key={c.id}
                      variant="outlined"
                      label={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <FlagImg code={c.id} />
                          <span style={{
                            fontWeight: 500,
                            fontStyle: 'normal',
                            fontSize: '16px',
                            lineHeight: '20px',
                            letterSpacing: '0px',
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            color: 'var(--Sem-text-colour-text-label-pressed, #002345)'
                          }}>{c.name}</span>
                        </Stack>
                      }
                      onDelete={() => toggle(c.id)}
                      deleteIcon={<CloseIcon sx={{ fontSize: 16, color: '#002345 !important' }} />}
                      size="small"
                      sx={{
                        height: 40,
                        gap: 1,
                        opacity: 1,
                        paddingTop: '8px',
                        paddingRight: '16px',
                        paddingBottom: '8px',
                        paddingLeft: '16px',
                        borderRadius: '9999px',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        backgroundColor:
                          'var(--Sem-object-fills-colour-bg-fill-secondary-pressed, #EFF0F2) !important',
                        borderColor: 'var(--Sem-brdr-colour-border-primary-pressed, #002345)',
                        '& .MuiChip-label': { paddingLeft: 0, paddingRight: 1 }
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              justifyContent: 'space-between',
              gap: { sm: 0, xs: 3 },
              flexDirection: { sm: 'row', xs: 'column-reverse' },
              opacity: 1,
              py: { sm: 2, xs: 2 },
              px: { sm: 2, xs: 2 },
              background: '#FFFFFF',
              boxShadow: '0px 0px 15px 0px #00000026'
            }}>
            {!isMobile && (
              <Typography sx={{ fontSize: '14px', color: '#6D6D6F' }}>
                {selected.size} of {totalCount} selected
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                selected.size === 0 || (!shouldShowIataStep && !hasChanges)
              }
              sx={{
                minWidth: '120px',
                minHeight: '44px',
                width: 'fit-content',
                padding: '12px 32px 12px 32px',
                gap: '6px',
                borderRadius: '9999px',
                backgroundColor: '#1460AA',
                textTransform: 'none',
                fontWeight: 700,
                lineHeight: '20px',
                fontSize: '16px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: '#FFFFFF',
                letterSpacing: '0px',
                '&:hover': {
                  backgroundColor: '#003D78'
                },
                '&:active': {
                  backgroundColor: '#002345'
                },
                '&:disabled': {
                  backgroundColor: '#D6D7D9',
                  color: '#6D6D6F'
                },
                '&:focus-visible': {
                  border: '1px solid #333333'
                }
              }}
            >
              {shouldShowIataStep ? 'Next' : 'Apply'}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent dividers sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            flex: 1,
            minHeight: 0,
            p: 0,
            overflow: 'auto'
          }}>
            <Grid maxWidth="1200px" width="100%" px={4} py={3} height="fit-content" container spacing={3} rowSpacing={4}>
              {iataCountries.map((c) => {
                const v = iataValues[c.id] || '';
                const invalid = applyAttempted && v.length > 0 && !iataValidate(v);
                return (
                  <Grid item xs={12} sm={6} md={4} key={c.id}>
                    <CommonTextField
                      label={`${c.name} IATA`}
                      value={v}
                      onChange={(val) => handleIataChange(c.id, val)}
                      startIcon={<FlagImg code={c.id} />}
                      error={invalid}
                      errorMessage={invalid ? 'The IATA number should consist of 8 digits' : undefined}
                      inputProps={{ inputMode: 'numeric', maxLength: 8 }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              justifyContent: 'space-between',
              gap: { sm: 0, xs: 3 },
              flexDirection: { sm: 'row', xs: 'column-reverse' },
              opacity: 1,
              py: { sm: 2, xs: 2 },
              px: { sm: 2, xs: 2 },
              background: '#FFFFFF',
              boxShadow: '0px 0px 15px 0px #00000026'
            }}>
            <Button
              variant="outlined"
              onClick={() => setStep(0)}
              sx={{
                minWidth: '120px',
                width: 'fit-content',
                padding: '12px 32px 12px 32px',
                gap: '6px',
                border: '1px solid #1460AA',
                borderRadius: '24px',
                backgroundColor: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 700,
                lineHeight: '20px',
                fontSize: '16px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: '#1460AA',
                letterSpacing: '0px',
                '&:hover': {
                  borderColor: "#003D78",
                  backgroundColor: '#F9F9F9'
                },
                '&:active': {
                  color: '#002345'
                },
                '&:disabled': {
                  backgroundColor: '#EFF0F2',
                  color: '#6D6D6F'
                },
                '&:focus-visible': {
                  border: '1px solid #333333'
                }
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!hasChanges}
              sx={{
                minWidth: '120px',
                minHeight: '44px',
                width: 'fit-content',
                padding: '12px 32px 12px 32px',
                gap: '6px',
                borderRadius: '9999px',
                backgroundColor: '#1460AA',
                textTransform: 'none',
                fontWeight: 700,
                lineHeight: '20px',
                fontSize: '16px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: '#FFFFFF',
                letterSpacing: '0px',
                '&:hover': {
                  backgroundColor: '#003D78'
                },
                '&:active': {
                  backgroundColor: '#002345'
                },
                '&:disabled': {
                  backgroundColor: '#D6D7D9',
                  color: '#6D6D6F'
                },
                '&:focus-visible': {
                  border: '1px solid #333333'
                }
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default PosModal;
